import React, { useMemo } from 'react';
import Col from '../Col';
import Row from '../Row';
import { ServiceType } from 'src/components/ContactForm/types';
import PropertySuiteSelector from 'src/components/ContactFormComponents/PropertySuiteSelector';
import ContactBasicInfo from 'src/components/ContactFormComponents/ContactBasicInfo';
import ContactAddress from 'src/components/ContactFormComponents/ContactAddress';
import ContactOptions from 'src/components/ContactFormComponents/ContactOptions';
import ServiceTypeSelector from 'src/components/ContactFormComponents/ServiceTypeSelector';
import RoleTileSelector from 'src/components/ContactFormComponents/RoleTileSelector';
import RolePropertiesSkeleton from 'src/components/ContactForm/skeletons/RolePropertiesSkeleton';
import CompanyDetails from 'src/components/ContactFormComponents/CompanyDetails';
import { IContact } from 'src/services/api/ServiceRequestDashboard/apiContracts';
import { useContactFormDataFromContext } from 'src/SharedCommonContext/ContactFormContext';
import './contact-form-content.scss';

interface ContactFormContentProps {
  types: ServiceType[];
  pageUsedFrom?: string;
  contactType: any;
  user: any;
  allowedRoles?: string[];
  homeOwnersRealtorsAndOccupantContacts: IContact[];
  propertyDevelopersAndServiceManagerContacts: IContact[];
  editingExistingContactInTable?: boolean;
  formType: any;
  setFormType: any;
  currentSuiteIdForAddingExistingHomeowner: string;
  isRoleDataLoading: boolean;
  onRoleChangeSuccess?: () => void;
}

const ContactFormContent = (props: ContactFormContentProps) => {
  const { pageUsedFrom } = props;
  const { contactFormData } = useContactFormDataFromContext();

  const isFromPropertyOrSuite: boolean = useMemo(() => {
    return pageUsedFrom === 'Properties' || pageUsedFrom === 'Suites';
  }, [pageUsedFrom]);

  const { contacts, contactDropdownHeader, showToggle } = useMemo(() => {
    if (pageUsedFrom === 'Properties' && !props.editingExistingContactInTable) {
      return {
        contacts: props.propertyDevelopersAndServiceManagerContacts,
        contactDropdownHeader: '(Select Contact)',
        showToggle: true,
      };
    }

    if (pageUsedFrom === 'Suites' && !props.editingExistingContactInTable) {
      return {
        contacts: props.homeOwnersRealtorsAndOccupantContacts,
        contactDropdownHeader: '(Select Contact)',
        showToggle: true,
      };
    }

    // Default case - Contacts page.
    return {
      contacts: [],
      contactDropdownHeader: '(Select Contact)',
      showToggle: false,
    };
  }, [
    pageUsedFrom,
    props.propertyDevelopersAndServiceManagerContacts,
    props.homeOwnersRealtorsAndOccupantContacts,
  ]);

  const contactOptions: any = useMemo(() => {
    return (
      contacts?.map((contact: any) => ({
        value: contact.id,
        label: `${contact.firstname} ${contact.lastname}`,
      })) || []
    );
  }, [contacts]);

  const selectedContact = useMemo(() => {
    if (contactFormData.id && props.formType === 'Existing') {
      return (
        contacts.find(
          (contact: IContact) => contact.id === contactFormData.id
        ) || null
      );
    }
    return null;
  }, [contactFormData.id, props.formType, contacts]);

  const selectedContactValue = useMemo(() => {
    if (contactFormData.id && props.formType === 'Existing') {
      const matchedContactOption: any= contactOptions.find(
        (option: any) => option.value === contactFormData.id
      );

      return matchedContactOption || null;
    }

    return null;
  }, [contactFormData.id, props.formType, contactOptions]);

  const selectedContactPhoneHasNoPhone: boolean = useMemo(() => {
    const foundContact: IContact = contacts?.find(
      (contact: IContact) => contact.id === selectedContactValue?.value
    );

    return !foundContact?.phone && props.formType === 'Existing';
  }, [selectedContactValue]);

  return (
    <div className="contact-form-content">
      <Row chonky>
        <Col chonky xs="12" md="6">
          <ContactBasicInfo
            formType={props.formType}
            setFormType={props.setFormType}
            contacts={contacts}
            contactDropdownHeader={contactDropdownHeader}
            showToggle={showToggle}
            contactOptions={contactOptions}
            selectedContactValue={selectedContactValue}
            pageUsedFrom={pageUsedFrom}
          />
          {selectedContactPhoneHasNoPhone && (
            <div
              style={{
                color: '#d0285b',
                marginBottom: '1.5rem',
                fontSize: '1.4rem',
                fontStyle: 'italic',
              }}
            >
              This contact doesn't have a phone number and can't be saved to
              this property yet. Please visit{' '}
              <a
                href="/app/contacts"
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  color: '#0c73c9',
                  textDecoration: 'underline',
                  fontWeight: '600',
                }}
              >
                Contacts
              </a>{' '}
              to add a phone number, then come back and try again.
            </div>
          )}
          <ContactAddress formType={props.formType} />
        </Col>
        <Col chonky xs="12" md="6">
          <RoleTileSelector
            isFromPropertyOrSuite={isFromPropertyOrSuite}
            allowedRoles={props.allowedRoles}
            formType={props.formType}
            selectedContact={selectedContact}
            editingExistingContactInTable={props.editingExistingContactInTable}
            isRoleDataLoading={props.isRoleDataLoading}
            onRoleChangeSuccess={props.onRoleChangeSuccess}
          />
          {props.isRoleDataLoading ? (
            <RolePropertiesSkeleton />
          ) : (
            <>
              <CompanyDetails />
              <PropertySuiteSelector
                user={props.user}
                isFromPropertyOrSuite={isFromPropertyOrSuite}
                selectedContact={selectedContact}
                formType={props.formType}
                currentSuiteIdForAddingExistingHomeowner={
                  props.currentSuiteIdForAddingExistingHomeowner
                }
              />
              <ServiceTypeSelector types={props.types} />
              <ContactOptions
                formType={props.formType}
                pageUsedFrom={pageUsedFrom}
              />
            </>
          )}
        </Col>
      </Row>
    </div>
  );
};

export default ContactFormContent;
